import { useEffect, useRef, useState } from 'react';
import { Link, Outlet, useLocation, useMatch } from 'react-router-dom';
import { LOCATIONS, PHOTO_COUNT, YEAR_RANGE } from '../data/locations';
import { Attribution } from '../components/Attribution';
import { PhotoGallery } from '../components/PhotoGallery';
import { SiteHeader, focusRing, pillClass } from '../components/SiteHeader';

/** Accessible 2D fallback / list view — works without WebGL. */
export function IndexGallery() {
  const photoMatch = useMatch('/list/:slug/photo/:photoId');
  const { hash } = useLocation();
  const [initialTarget] = useState(() => photoMatch?.params.slug ?? decodeURIComponent(hash.slice(1)));
  const [active, setActive] = useState(LOCATIONS[0].id);
  const [navHeight, setNavHeight] = useState(0);
  const navRef = useRef<HTMLElement>(null);
  const chipsRef = useRef<HTMLUListElement>(null);
  const scrolledToTarget = useRef(false);

  useEffect(() => {
    if (!initialTarget || !navHeight || scrolledToTarget.current) return;
    scrolledToTarget.current = true;
    document.getElementById(initialTarget)?.scrollIntoView({ block: 'start', behavior: 'instant' });
  }, [initialTarget, navHeight]);

  useEffect(() => {
    const nav = navRef.current;
    if (!nav) return;
    const observer = new ResizeObserver(() => setNavHeight(nav.offsetHeight));
    observer.observe(nav);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    let frame = 0;
    const update = () => {
      const line = (navRef.current?.getBoundingClientRect().bottom ?? 0) + 32;
      const current = LOCATIONS.reduce<string>((found, loc) => {
        const top = document.getElementById(loc.id)?.getBoundingClientRect().top ?? Infinity;
        return top <= line ? loc.id : found;
      }, LOCATIONS[0].id);
      setActive(current);
    };
    const onScroll = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(update);
    };
    update();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
    return () => {
      cancelAnimationFrame(frame);
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
    };
  }, []);

  useEffect(() => {
    const list = chipsRef.current;
    const chip = list?.querySelector<HTMLElement>(`[data-slug="${active}"]`);
    if (!list || !chip || list.scrollWidth <= list.clientWidth) return;
    list.scrollTo({ left: chip.offsetLeft - (list.clientWidth - chip.offsetWidth) / 2 });
  }, [active]);

  return (
    <div className="min-h-dvh bg-[#05070c] text-white">
      <SiteHeader action={{ to: '/', label: 'Globe view' }} />

      <div className="mx-auto max-w-7xl px-5 pb-8 pt-8 sm:px-8 lg:pt-12">
        <p className="font-mono text-[10px] uppercase tracking-[0.16em] text-[#f2c66d]/90">List view</p>
        <h1 className="mt-2 text-[32px] font-[560] leading-[1.1] tracking-tight lg:text-[40px]">Every place</h1>
        <p className="mt-3 font-mono text-[10px] uppercase tracking-[0.16em] text-white/55">
          {LOCATIONS.length} places · {PHOTO_COUNT} photographs · {YEAR_RANGE}
        </p>
      </div>

      <nav ref={navRef} aria-label="Places" className="sticky top-0 z-20 border-y border-white/10 bg-[#05070c]/95">
        <ul
          ref={chipsRef}
          className="mx-auto flex max-w-7xl gap-1.5 overflow-x-auto px-5 py-2.5 [scrollbar-width:none] sm:flex-wrap sm:px-8"
        >
          {LOCATIONS.map((loc) => (
            <li key={loc.id} className="shrink-0">
              <a
                href={`#${loc.id}`}
                data-slug={loc.id}
                aria-current={active === loc.id ? 'location' : undefined}
                className={`inline-flex h-9 items-center rounded-full pointer-coarse:h-11 border px-3 text-[13px] transition-colors ${
                  active === loc.id
                    ? 'border-[#f2c66d]/60 bg-[#f2c66d]/10 text-[#f2c66d]'
                    : 'border-white/12 text-white/70 hover:bg-white/10 hover:text-white'
                } ${focusRing} focus-visible:-outline-offset-2`}
              >
                {loc.name}
              </a>
            </li>
          ))}
        </ul>
      </nav>

      <main className="mx-auto max-w-7xl px-5 sm:px-8">
        {LOCATIONS.map((loc, i) => (
          <section
            key={loc.id}
            id={loc.id}
            aria-labelledby={`${loc.id}-name`}
            style={{ scrollMarginTop: navHeight }}
            className="border-t border-white/10 pb-14 pt-10 first:border-t-0"
          >
            <div className="flex flex-wrap items-end justify-between gap-x-8 gap-y-5">
              <div>
                <p className="font-mono text-[10px] uppercase tracking-[0.16em] text-[#f2c66d]/90">{loc.country}</p>
                <h2
                  id={`${loc.id}-name`}
                  className="mt-2 text-[28px] font-[580] leading-[1.05] tracking-[-0.02em] lg:text-[34px]"
                >
                  {loc.name}
                </h2>
                <p className="mt-3 font-mono text-[10px] uppercase tracking-[0.16em] text-white/55">
                  {loc.date} · {loc.photos.length} photographs
                </p>
                <p className="mt-3 max-w-[60ch] text-[15px] leading-[1.6] text-white/70">{loc.blurb}</p>
              </div>
              <Link to={`/city/${loc.id}`} className={pillClass}>
                Show on globe
              </Link>
            </div>
            <div className="mt-6">
              <PhotoGallery
                photos={loc.photos}
                placeName={loc.name}
                hrefFor={(p) => `/list/${loc.id}/photo/${p.id}`}
                eager={i === 0 ? 6 : 0}
              />
            </div>
          </section>
        ))}
      </main>

      <footer className="mx-auto flex max-w-7xl flex-wrap items-center justify-between gap-4 border-t border-white/10 px-5 py-8 sm:px-8">
        <Attribution />
        <Link to="/" className={pillClass}>
          Back to the globe
        </Link>
      </footer>

      <Outlet />
    </div>
  );
}
